import React, { useState, useEffect } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
import PortalTooltip from '../../../../components/PortalTooltip/PortalTooltip';
import { DocumentInfo } from '../../../../models/DocumentInfo/DocumentInfo';
import moment from 'moment';
import '../../../../components/DocumentListCard/DocumentListCard.scss';
import '../../../../components/DocumentTile/DocumentTile.scss';
import './ConnectMarketplaceAgreements.scss';
import { SignedBuyerNda } from '../../../../slices/marketplace/timeline/models/signed-buyer-nda.model';
import { useLegalDocument } from '../../../../custom-hooks/useLegalDocument';
import { LegalDocumentType } from '../../../../models/LegalDocuments/legal-document-type.enum';
import { BuyersNdaStatusResponse } from '../../../../slices/marketplace/timeline/models/buyers-nda-status.model';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import createNotification from '../../../../utils/createNotification';
import { BuyerNdaSign } from '../../../../components/BuyerNdaSign/BuyerNdaSign';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import '../../../../components/DocumentSignModal/DocumentSignModal.scss';

const NDA_DOCUMENT_FLAG = `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.ConnectAgreementsCode}.${NotificationFlagsCategories.NdaDocumentCode}`;
const NDA_REDLINE_FLAG = `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.ConnectAgreementsCode}.${NotificationFlagsCategories.NdaRedlineDocumentCode}`;

interface ConnectMarketplaceAgreementsProps {
    ndaStatus: BuyersNdaStatusResponse | null;
    onNdaSigned: () => void;
}

const ConnectMarketplaceAgreements = (props: ConnectMarketplaceAgreementsProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [ndaDocuments, setNdaDocuments] = useState<DocumentInfo[]>([]);
    const [signingDocumentId, setSigningDocumentId] = useState<number | null>(null);
    const { documentUrl: buyerTermsUrl } = useLegalDocument(
        LegalDocumentType.BuyersTermsOfService,
        axios
    );

    const getDocuments = async () => {
        const newDocumentList: DocumentInfo[] = [];

        try {
            const response = await axios.get('/api/Marketplace/MarketplaceBuyersNda/signed');
            const signedNdas: SignedBuyerNda[] = response.data || [];

            signedNdas.forEach((nda) => {
                newDocumentList.push({
                    name: nda.documentName,
                    source: nda.documentFileUrl,
                    date: nda.documentSignedDate
                        ? new Date(nda.documentSignedDate)
                        : undefined,
                });
            });
        } catch (error) {
            console.log('Error checking buyer NDA status:', error);
        }

        setNdaDocuments(newDocumentList);
    };

    const getTooltipId = (index: number, name: string) => {
        return `agreement-tooltip-${index}-${name.replace(/\s+/g, '-')}`;
    };

    const openFile = (source: string) => {
        if (source && source !== '#') {
            window.open(source, '_blank');
        }
    };

    const handleExecuteDocument = async () => {
        if (props.ndaStatus?.isInternalTeamMember) {
            createNotification(
                'You don\'t have rights for execution of the Document. The NDA should be executed by the primary buyer.',
                'warning'
            );
            return;
        }
        try {
            const response = await axios.post('/api/Marketplace/MarketplaceBuyersNda/sign', []);
            setSigningDocumentId(response.data);
        } catch (error) {
            createNotification(
                'An error occurred while preparing the document for signing. Please try again.',
                'error'
            );
        }
    };

    const handleSignComplete = () => {
        createNotification('NDA signed successfully!', 'success');
        setSigningDocumentId(null);
        props.onNdaSigned();
        getDocuments();
    };

    useEffect(() => {
        getDocuments();
    }, []);

    const documentList: DocumentInfo[] = [
        ...ndaDocuments,
        ...(buyerTermsUrl ? [{
            name: 'Connect Buyers TOS Addendum',
            source: buyerTermsUrl,
            date: new Date('2026-01-30'),
        }] : []),
    ];

    const hasUnsignedNda = props.ndaStatus &&
        !props.ndaStatus.hasSignedLatestNda &&
        props.ndaStatus.hasActiveNdaTemplate &&
        props.ndaStatus.isReturningBuyer;

    const redlineDocumentUrl = props.ndaStatus?.redlineDocumentUrl ?? null;
    const ndaTemplateName = props.ndaStatus?.ndaTemplateName ?? 'Buyer NDA';

    const renderDocumentTile = (
        item: DocumentInfo,
        index: number,
        notificationFlagCode?: string
    ) => (
        <div
            key={`document-${index}-${item.name}`}
            className="document-tile-wrapper">
            {notificationFlagCode && (
                <NotificationFlag
                    flagClassName="document-notification-flag"
                    eventCode={notificationFlagCode}
                    includePrefix={true}
                    doNotRemove={true}
                />
            )}
            <div
                style={{ width: 142, height: 60 }}
                className="document-tile"
                onClick={() => openFile(item.source)}>
                <div className="icon-container">
                    <FontAwesomeIcon
                        inverse
                        className="icon"
                        icon={faFileAlt as any}
                    />
                </div>
                <div className="file-info">
                    <div>
                        <div
                            className="file-name"
                            id={getTooltipId(index, item.name)}>
                            {item.name}
                        </div>
                        {item.date ? (
                            <div className="file-date">
                                {moment(item.date).format('L')}
                            </div>
                        ) : null}
                    </div>
                    <PortalTooltip
                        anchorId={getTooltipId(index, item.name)}
                        delayShow={300}
                        place="top"
                        content={item.name}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="connect-marketplace-agreements">
            {hasUnsignedNda && (
                <>
                    <h5 className="section-header">New Documents</h5>
                    <div className="documents-container">
                        <div className="new-document-wrapper">
                            {renderDocumentTile(
                                { name: ndaTemplateName, source: '#' },
                                -1,
                                NDA_DOCUMENT_FLAG
                            )}
                            <button
                                className="btn btn-primary execute-document-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExecuteDocument();
                                }}>
                                Execute Document
                            </button>
                        </div>
                        {redlineDocumentUrl &&
                            renderDocumentTile(
                                { name: `${ndaTemplateName} — Redline`, source: redlineDocumentUrl },
                                -2,
                                NDA_REDLINE_FLAG
                            )}
                    </div>
                </>
            )}
            {hasUnsignedNda && <h5 className="section-header">Documents</h5>}
            <div className="documents-container">
                {documentList.map((item, index) =>
                    renderDocumentTile(item, index)
                )}
            </div>

            {signingDocumentId && (
                <Rodal
                    className="document-sign-modal"
                    visible={true}
                    onClose={() => setSigningDocumentId(null)}>
                    <ModalHeader>Sign Document</ModalHeader>
                    <ModalBody>
                        <BuyerNdaSign
                            documentId={signingDocumentId}
                            onDocumentSign={handleSignComplete}
                        />
                        <div className="buttons-wrapper">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => setSigningDocumentId(null)}>
                                Cancel
                            </button>
                        </div>
                    </ModalBody>
                </Rodal>
            )}
        </div>
    );
};

export default ConnectMarketplaceAgreements;
